/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */

// Split out of kHTML.ts 2026-10-19 (owner: smaller units for the reuse reviewer). kHTML.ts
// re-exports kSelect, so `import { kSelect } from "./kHTML"` keeps working.
import ks from "../ReUsableTS/kBrowserShared";
import LNG from "./kLNG";
import { kFormTheme } from "./kFormTheme";
import { kSelectCore } from "../kReusableCoreTS/kHTMLWidgetsCore";

// Extends the server-safe kSelectCore: the Choices model + selected
// value + the static <select> HTML (ToHtmlString) live in the base
// (Choices is inherited). This browser subclass keeps the live DOM
// <select> and its change wiring.
export class kSelect extends kSelectCore {
    public Select: HTMLSelectElement;
    public ChangeCB: any = null;
    public NewCB: any = null;
    public Editable: boolean = false;
    public DataList: HTMLDataListElement | null = null;
    public constructor(ID?: string) {
        super(ID);
        this.Select = document.createElement("select");
        this.Select.addEventListener("change", this._SelectChange);
        // .kField carries the radius that used to be inline, plus the border, hover and
        // focus ring, and draws OUR chevron instead of the per-platform native arrow.
        // Deliberately a CLASS, not inline: the note below about inline height applies
        // equally to any inline style - a class stays overridable by the consumer.
        kFormTheme.Apply(this.Select, "kField");
        // NOTE: do NOT force an inline height here. An inline style beats any
        // consumer CSS class, so a fixed height combined with a consumer's
        // box-sizing:border-box + vertical padding collapses the content box to ~0px
        // and CLIPS the selected option text to invisible (the value is set but
        // cannot be seen - looks like "the pick doesn't stick"). Let native sizing /
        // the consumer's CSS control the height. (Root-caused 2026-06-27.)
        if (ks.KNotNullOrUndefined(ID)) {
            this.Select.id = ks.Nz(ID);
        }
    }
    public SetEditable = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.Editable = true;
            this.DataList = document.createElement("datalist");
            this.Select.appendChild(this.DataList);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.SetEditable");
        }
    };
    public SetMulti = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Select) {
                this.Select.multiple = true;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.SetMulti");
        }
    };
    public ClearChoices = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.Choices = [];
            this.Select.innerHTML = "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.ClearChoices");
        }
    };
    /** The first selected option of the underlying select, or null when there is none. The
        KGlobalErrorHandler label is the caller's own. */
    private _FirstSelOption = (Label: string): HTMLOptionElement | null => {
        if (ks.KErrorMode === true) {
            return null;
        }
        try {
            if (this.Select) {
                const SelectedOptions = this.Select.selectedOptions;
                if (SelectedOptions) {
                    return SelectedOptions.item(0);
                }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect._FirstSelOption");
            return null;
        }
    };
    public GetSelValue = (): string => {
        if (ks.KErrorMode === true) {
            return "";
        }
        try {
            const SelOption1: HTMLOptionElement | null = this._FirstSelOption("kSelect.GetSelValue");
            if (SelOption1 !== null) {
                return ks.Nz(SelOption1.textContent);
            }
            return "";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.GetSelValue");
            return "";
        }
    };
    public GetSelIndex = (): number => {
        if (ks.KErrorMode === true) {
            return 0;
        }
        try {
            const SelOption1: HTMLOptionElement | null = this._FirstSelOption("kSelect.GetSelIndex");
            if (SelOption1 !== null) {
                return ks.N0(SelOption1.index);
            }
            return 0;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.GetSelIndex");
            return 0;
        }
    };
    public AddChoices = (NewChoices: string[],AlreadySorted?:boolean): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            let CurrChoice = "";
            if(AlreadySorted !== true){
                NewChoices = NewChoices.sort();
            }
            for (CurrChoice of NewChoices) {

                this.AddChoice(CurrChoice);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.AddChoices");
        }
    };
    // public AddChoiceWithIcon = (ChoiceTitle: string,IconBase64:string): void => {
    //     const NewElement: HTMLOptionElement = this.AddChoice(ChoiceTitle);
    //     if (ks.KErrorMode === true) {return;}
    //     try {
    //         const ChoiceStyle:CSSStyleDeclaration = NewElement.style;
    //         ChoiceStyle.background = "url('" + IconBase64 +"')";
    //     } catch (e) {
    //         ks.KGlobalErrorHandler(e, "kSelect.AddChoiceWithIcon");
    //     }
    // };
    /** Registers one built option with the select or datalist and records its title in Choices.
        The KGlobalErrorHandler label is the caller's own. */
    private _RegisterChoiceOption = (Option: HTMLOptionElement, Title: string, Label: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (null !== this.Select) {
                if (this.Editable === true) {
                    if (this.DataList) {
                        this.DataList.appendChild(Option);
                    }
                } else {
                    this.Select.add(Option);
                }
            }
            this.Choices.push(Title);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect._RegisterChoiceOption");
            return;
        }
    };
    public AddChoice = (ChoiceTitle: string): HTMLOptionElement => {
        if (ks.KErrorMode === true) {
            return document.createElement("option");
        }
        try {
            const NewElement: HTMLOptionElement = document.createElement("option");
            NewElement.textContent = ChoiceTitle;
            this._RegisterChoiceOption(NewElement, ChoiceTitle, "kSelect.AddChoice");
            return NewElement;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.AddChoice");
            return document.createElement("option");
        }
    };
    public AddChoiceElement = (Elem:HTMLElement,Title:string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            const NewOption: HTMLOptionElement = document.createElement("option");
            NewOption.appendChild(Elem);
            NewOption.title = Title;
            this._RegisterChoiceOption(NewOption, Title, "kSelect.AddChoiceElement");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.AddChoiceElement");
        }
    };
    public GetOption = (Title:string): HTMLOptionElement |null => {
        if (ks.KErrorMode === true) {
            return null;
        }
        try {
            
            let CurrOption: HTMLOptionElement;
            for (CurrOption of Array.from(this.Select.options)) {
                if (CurrOption.title === Title) {
                    return CurrOption;
                }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.GetOption");
            return null;
        }
    };
    private _SelectChange = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Select !== null) {
                if (this.Select.value === LNG.NEW) {
                    ks.DoCallBack(this.NewCB, this.Select.value, false);
                } else {
                    ks.DoCallBack(this.ChangeCB, this.Select.value, false);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect._SelectChange");
        }
    };
    public SetSelectedItemByIndex = (Index:number): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            const ToBeSelectedOption: HTMLOptionElement | null = <HTMLOptionElement>this.Select.item(Index);
            if(ToBeSelectedOption){
                ToBeSelectedOption.selected = true;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.SetSelectedItemByIndex");
        }
    };
    public SetSelectedItem = (SelectedTitle: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            let NewElement: HTMLOptionElement | null = null;
            let i = 0;
            if (null !== this.Select) {
                for (i = 0; i < this.Select.childElementCount; i++) {
                    NewElement = <HTMLOptionElement>this.Select.item(i);
                    if (NewElement.textContent === SelectedTitle) {
                        NewElement.selected = true;
                        break;
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.SetSelectedItem");
        }
    };
    public AddNewOption = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.AddChoice(LNG.ADD);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kSelect.AddNewOption");
        }
    };
}
